import { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { pngBase64ToSrc, svgMarkupToDataUrl } from "./planSymbolUtils";

type PreviewStatus = "empty" | "loading" | "ready" | "error";

export function PlanSymbolPreview({
  svgMarkup,
  pngBase64,
  title,
  className,
}: {
  svgMarkup: string;
  pngBase64: string;
  title?: string;
  className?: string;
}) {
  const trimmedSvg = String(svgMarkup || "").trim();
  const svgDataUrl = useMemo(() => svgMarkupToDataUrl(trimmedSvg), [trimmedSvg]);
  const pngSrc = useMemo(() => pngBase64ToSrc(pngBase64), [pngBase64]);

  const [status, setStatus] = useState<PreviewStatus>("empty");

  useEffect(() => {
    if (trimmedSvg) {
      setStatus("loading");
      return;
    }
    if (pngSrc) {
      setStatus("ready");
      return;
    }
    setStatus("empty");
  }, [trimmedSvg, pngSrc]);

  const showSvg = Boolean(trimmedSvg && svgDataUrl);
  const showPng = !showSvg && Boolean(pngSrc);

  return (
    <div className={clsx("mb-ps-preview", className)}>
      <div className="mb-ps-preview__stage" data-status={status}>
        {showSvg ? (
          <img
            key={svgDataUrl}
            src={svgDataUrl}
            alt={title?.trim() ? `Vorschau: ${title}` : "SVG-Vorschau"}
            className="mb-ps-preview__img"
            draggable={false}
            onLoad={() => setStatus("ready")}
            onError={() => setStatus("error")}
          />
        ) : showPng ? (
          <img
            src={pngSrc}
            alt={title?.trim() ? `Vorschau: ${title}` : "PNG-Vorschau"}
            className="mb-ps-preview__img"
            draggable={false}
          />
        ) : status === "error" ? (
          <div className="mb-ps-preview__message mb-ps-preview__message--error">
            <span className="mb-ps-preview__message-icon" aria-hidden>
              !
            </span>
            <span>Ungültiges SVG</span>
          </div>
        ) : (
          <div className="mb-ps-preview__message">
            <span className="mb-ps-preview__message-icon" aria-hidden>
              ◇
            </span>
            <span>SVG einfügen oder hochladen</span>
          </div>
        )}

        {status === "loading" && showSvg ? (
          <div className="mb-ps-preview__loading" aria-hidden />
        ) : null}
      </div>

      <div className="mb-ps-preview__meta">
        <span className="mb-ps-preview__label">Live-Vorschau</span>
        {title?.trim() ? (
          <span className="mb-ps-preview__title">{title.trim()}</span>
        ) : (
          <span className="mb-ps-preview__title mb-ps-preview__title--muted">Ohne Bezeichnung</span>
        )}
      </div>
    </div>
  );
}
